import React, { createContext, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { IForm } from '../../../../../../../libs/uiKit/fieldsBuilder/types';
import { ISelectItem } from '../../../../../../../libs/uiKit/select/types';
import { IDaDataValue } from '../../../../../../../libs/uiKit/daData/types';
import useGetDicti from '../../../../../common/hooks/useGetDicti';
import { DictKeyEnum } from '../../../../../../../libs/api/graphqlTypes';
import Button from '../../../../../../../libs/uiKit/button';
import {
	ALARM_IS_REQUIRED_FOR, PROPERTY_APARTMENTS,
	PROPERTY_BATHHOUSE,
	PROPERTY_FLAT,
	PROPERTY_GARAGE,
	PROPERTY_GUEST_HOUSE,
	PROPERTY_HIDE_WOOD_ELEMENTS_FOR,
	PROPERTY_HIDE_YEAR_FOR,
	PROPERTY_HOUSE,
	PROPERTY_LAND, PROPERTY_MATERIAL_SHOW_FOR,
	PROPERTY_NON_RESIDENTIAL,
	PROPERTY_PARKING, PROPERTY_STORAGE,
	PROPERTY_TOWNHOUSE,
	RADIO_BOOLEAN
} from '../../../../../../../libs/utils/staticData';
import {useCreateAgreementContext} from "../../../../ui/context";

type PropertyContext = {
	form: IForm;
};

export type PropertyContextProps = {
	children?: React.ReactNode;
	isFull: boolean;
	number: number;
	defaultValues?: PropertyField;
	arrayName: string;
	propertiesCount?: number;
};

export type PropertyField = {
	pledge?: ISelectItem | null;
	pledgeAddress?: IDaDataValue | null;
	addressMatch?: string;
	pledgeAddressEgrn?: string;
	pledgeShare?: number;
	agency?: string;
	subjectCost?: number;
	nameOfAppraiser?: string;
	subjectCostByContract?: number;
	area?: number;
	roomsCount?: number;
	objectOfUnfinishedConstruction?: string;
	repairPlanning?: string;
	repairIntervalStart?: Date | null;
	repairIntervalEnd?: Date | null;
	riskCoefficient?: number;
	basementOrAttic?: string;
	floor?: number;
	floorCount?: number;
	yearOfBuild?: string;
	yearOfCapitalRepair?: string;
	wallMaterial?: ISelectItem | null;
	floorMaterial?: ISelectItem | null;
	woodInConstruction?: string;
	interiorDecoration?: string;
	replanPlanning?: string;
	replanIntervalStart?: Date | null;
	replanIntervalEnd?: Date | null;
	rent?: string;
	damage?: string;
	alarm?: ISelectItem | null;
	alarmInput?: string;
	openFire?: ISelectItem | null;
	openFireInput?: string;
	additionalRisks?: ISelectItem | null;
	additionalRisksInput?: string;
	cadastralNumber?: string;
	purpose?: ISelectItem | null;
    isRegistrationAddress?: boolean;
    isResidenceAddress?: boolean;
};

const PropertyContext = createContext<PropertyContext>({} as PropertyContext);

export const usePropertyContext = (): PropertyContext => useContext(PropertyContext);

/*const FLAT_ISN: string = '654391';
const FLOOR_REQUIRED: string[] = [FLAT_ISN, '1867861', '833631'];
const FLOOR_COUNT_HIDDEN: string[] = ['654411', '654421'];
const ROOM_REQUIRED: string[] = [FLAT_ISN, PROPERTY_HOUSE];*/

const PropertyContextProvider: React.FC<PropertyContextProps> = props => {
	const { children, defaultValues, number, isFull, arrayName, propertiesCount } = props;
	const formHook = useFormContext();
	const { data: selectPledge, loading: loadingPledge } = useGetDicti(DictKeyEnum.PropertyType);
	const { data: selectWallMaterial, loading: loadingWallMaterial } = useGetDicti(DictKeyEnum.WallMaterial);
	const { data: selectFloorMaterial, loading: loadingFloorMaterial } = useGetDicti(DictKeyEnum.FloorMaterial);
	const { data: selectFireSources, loading: loadingFireSources } = useGetDicti(DictKeyEnum.FireSources);
	const { data: selectAlarms, loading: loadingAlarms } = useGetDicti(DictKeyEnum.Alarms);
	const { data: selectPurposes, loading: loadingPurposes } = useGetDicti(DictKeyEnum.Purpose);
	const { data: selectRiskFactors, loading: loadingRiskFactors} = useGetDicti(DictKeyEnum.RiskFactors);

	const {
		isFieldsDisabled,
		calcForm,
		showFields,
	} = useCreateAgreementContext();

	const [stateAddressEgrn, setAddressEgrn] = React.useState<boolean>(false);

	const [
		repairPlanningValue,
		replanPlanningValue,
		pledgeValue,
		alarmValue,
		openFireValue,
		additionalRisksValue,
		floorCountValue,
		wallMaterialValue,
		floorMaterialValue,
		yearOfBuildWatch,
		addressMatchValue,
		insureTypeValue
	] = formHook.watch([
		`${arrayName}[${number}].repairPlanning`,
		`${arrayName}[${number}].replanPlanning`,
		`${arrayName}[${number}].pledge`,
		`${arrayName}[${number}].alarm`,
		`${arrayName}[${number}].openFire`,
		`${arrayName}[${number}].additionalRisks`,
		`${arrayName}[${number}].floorCount`,
		`${arrayName}[${number}].wallMaterial`,
		`${arrayName}[${number}].floorMaterial`,
		`${arrayName}[${number}].yearOfBuild`,
		`${arrayName}[${number}].addressMatch`,
		'insureType'
	]);

	const yearOfBuildValue = (typeof yearOfBuildWatch === 'string') ? parseInt(yearOfBuildWatch, 10) : yearOfBuildWatch;

	const isPlanningRepair: boolean = repairPlanningValue === 'true';
	const isPlanningReplan: boolean = replanPlanningValue === 'true';
	const isAlarmOther: boolean = alarmValue?.value === '2127161';
	const isOpenFireOther: boolean = openFireValue?.value === '2127121';
	const isAdditionalRisksOther: boolean = additionalRisksValue?.value === '2127211';
	const pledgeIsn: string | undefined = pledgeValue?.value;
	const isFlat: boolean = PROPERTY_FLAT === pledgeIsn;

	const {
		pledge,
		pledgeAddress,
		addressMatch,
		pledgeAddressEgrn,
		pledgeShare,
		agency,
		subjectCost,
		nameOfAppraiser,
		subjectCostByContract,
		area,
		roomsCount,
		objectOfUnfinishedConstruction,
		repairPlanning,
		repairIntervalStart,
		repairIntervalEnd,
		replanIntervalStart,
		replanIntervalEnd,
		riskCoefficient,
		basementOrAttic,
		floor,
		floorCount,
		yearOfBuild,
		yearOfCapitalRepair,
		wallMaterial,
		floorMaterial,
		interiorDecoration,
		replanPlanning,
		rent,
		damage,
		alarm,
		alarmInput,
		openFire,
		openFireInput,
		additionalRisks,
		additionalRisksInput,
		cadastralNumber,
		woodInConstruction,
		purpose
	} = defaultValues || {};

	React.useEffect(() => {
		if (!pledgeAddressEgrn) return;
		setAddressEgrn(true);
	}, [pledgeAddressEgrn]);

	const forceWoodInConstruction = (wallMaterialValue?.value === '2084991' || floorMaterialValue?.value === '2084991');

	const woodElementsHidden = PROPERTY_HIDE_WOOD_ELEMENTS_FOR.includes(String(pledgeIsn)) || PROPERTY_MATERIAL_SHOW_FOR.includes(String(pledgeIsn));

	const phAddress = calcForm?.policyholder?.registrationAddress?.value;

	const form: IForm = React.useMemo(
		() => ({
			formHook,
			//isDisabled: isFieldsDisabled,
			hotReload: true,
			fields: [
				{
					isHidden: ![PROPERTY_FLAT, PROPERTY_TOWNHOUSE, PROPERTY_HOUSE].includes(String(pledgeIsn)),
					label: 'Адрес регистрации страхователя соответствует адресу объекта страхования',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].addressMatch`,
						items: RADIO_BOOLEAN,
						defaultValue: addressMatch,
					}
				},
				{
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].pledge`,
						placeholder: 'Предмет залога',
						isRequired: true,
						errorMessage: 'Выберите предмет залога',
						isLoading: loadingPledge,
						defaultValue: pledge,
						items: selectPledge,
						grid: 3,
					},
				},

				{
					isHidden: addressMatchValue === 'true',
					field: {
						fieldType: 'daData',
						fieldName: `${arrayName}[${number}].pledgeAddress`,
						daDataType: 'address',
						placeholder: 'Адрес приобретаемого / закладываемого объекта залога',
						isRequired: true,
						validationType: isFlat ? 'addressWithFlat' : undefined,
						defaultValue: pledgeAddress,
						errorMessage: 'Введите адрес объекта залога',
						grid: 9,
					},
				},
				{
					isHidden: addressMatchValue !== 'true',
					field: {
						fieldType: 'input',
						fieldName: `${arrayName}[${number}].pledgeAddressDisabled`,
						placeholder: 'Адрес приобретаемого / закладываемого объекта залога',
						isDisabled: true,
						defaultValue: phAddress,
						grid: 9,
					},
				},
				{
					field: {
						fieldType: 'input',
						fieldName: `${arrayName}[${number}].cadastralNumber`,
						placeholder: 'Кадастровый номер',
						validationType: 'cadastralNumber',
						defaultValue: cadastralNumber,
						isRequired: ![PROPERTY_FLAT, PROPERTY_APARTMENTS, PROPERTY_TOWNHOUSE].includes(String(pledgeIsn)),
					},
				},
				{
					field: {
						fieldType: 'element',
						fieldName: `${arrayName}[${number}].showIsErgn`,
						element: (
							<Button appearance='link' onClick={(): void => setAddressEgrn(state => !state)}>
								Ввести адрес как в ЕГРН
							</Button>
						),
					},
				},
				{
					isHidden: !stateAddressEgrn,
					field: {
						fieldType: 'input',
						fieldName: `${arrayName}[${number}].pledgeAddressEgrn`,
						placeholder: 'Ввод адреса из ЕГРН',
						defaultValue: pledgeAddressEgrn,
					},
				},
				{
					isHidden: propertiesCount === 1,
					field: {
						fieldType: 'range',
						fieldName: `${arrayName}[${number}].pledgeShare`,
						placeholder: 'Доля приобретаемого / закладываемого объекта залога',
						isRequired: true,
						min: 0,
						max: 100,
						step: 1,
						defaultValue: pledgeShare,
						errorMessage: 'Выберите долю',
					},
				},
				{
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].area`,
						defaultValue: area || '',
						digitsAfterDot: 2,
						grid: 6,
						placeholder: 'Общая площадь, м2',
						isRequired: true,
						validationType: 'minMax',
						maxValue: 1000,
						errorMessage: 'Введите площадь',
					},
				},
				{
					isHidden: [PROPERTY_LAND, PROPERTY_PARKING].includes(String(pledgeIsn)),
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].roomsCount`,
						placeholder: 'Количество комнат',
						grid: 6,
						isRequired: true,
						validationType: 'minMax',
						minValue: 1,
						defaultValue: roomsCount,
					},
				},
				{
					isHidden: [PROPERTY_TOWNHOUSE, PROPERTY_LAND, PROPERTY_GARAGE, PROPERTY_HOUSE, PROPERTY_GUEST_HOUSE, PROPERTY_BATHHOUSE].includes(String(pledgeIsn)),
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].floor`,
						defaultValue: floor,
						isRequired: true,
						grid: 6,
						errorMessage: 'Введите этаж',
						validationType: 'minMax',
						minValue: [PROPERTY_PARKING, PROPERTY_STORAGE].includes(String(pledgeIsn)) ? -floorCountValue : 1,
						maxValue: floorCountValue,
						placeholder: 'Этаж',
						minMaxMessage: 'Этаж квартиры не должен превышать общую этажность строения'
					},
				},
				{
					isHidden: [PROPERTY_LAND].includes(String(pledgeIsn)),
					field: {
						fieldType: 'number',
						isRequired: true,
						grid: 6,
						errorMessage: 'Введите этажность строения',
						fieldName: `${arrayName}[${number}].floorCount`,
						defaultValue: floorCount,
						placeholder: 'Этажность строения',
					},
				},
				{
					isHidden: PROPERTY_HIDE_YEAR_FOR.includes(String(pledgeIsn)),
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].yearOfBuild`,
						placeholder: 'Год постройки (примерный)',
						grid: 6,
						validationType: 'minMax',
						minValue: 1800,
						maxValue: Number(dayjs().format('YYYY')) + 1,
						defaultValue: yearOfBuild,
						isRequired: true,
						errorMessage: 'Введите год',
					},
				},
				{
					isHidden: [PROPERTY_LAND, PROPERTY_PARKING].includes(String(pledgeIsn)) || !isFull || !yearOfBuildValue || yearOfBuildValue < 1000 || Number(dayjs().format('YYYY')) - yearOfBuildValue <= 50,
					field: {
						fieldType: 'number',
						grid: 6,
						fieldName: `${arrayName}[${number}].yearOfCapitalRepair`,
						placeholder: 'Год капитального ремонта',
						defaultValue: yearOfCapitalRepair,
						validationType: 'minMax',
						isRequired: true,
						minValue: parseInt(yearOfBuildValue, 10) || 1800,
						//maxValue: Number(dayjs().format('YYYY')) + 10,
					},
				},
				{
					isHidden: !PROPERTY_MATERIAL_SHOW_FOR.includes(String(pledgeIsn)),
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].wallMaterial`,
						isRequired: true,
						grid: woodElementsHidden ? 6 : 4,
						items: selectWallMaterial,
						isLoading: loadingWallMaterial,
						defaultValue: wallMaterial,
						placeholder: 'Материал стен',
					},
				},
				{
					isHidden: !PROPERTY_MATERIAL_SHOW_FOR.includes(String(pledgeIsn)),
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].floorMaterial`,
						isRequired: true,
						grid: woodElementsHidden ? 6 : 4,
						items: selectFloorMaterial,
						isLoading: loadingFloorMaterial,
						defaultValue: floorMaterial,
						placeholder: 'Материал межэтажных перекрытий',
					},
				},
				{
					isHidden: woodElementsHidden,
					label: 'Наличие деревянных элементов',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].woodInConstruction`,
						items: RADIO_BOOLEAN,
						defaultValue: forceWoodInConstruction ? 'true' : (woodInConstruction || 'false'),
						value: forceWoodInConstruction ? 'true' : undefined,
						grid: 4,
					},
				},
				{
					isHidden: !showFields.includes('subjectCost'),
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].subjectCost`,
						digitsAfterDot: 2,
						grid: 6,
						defaultValue: subjectCost || '',
						placeholder: 'Рыночная стоимость объекта залога (согласно отчету об оценке)',
						isRequired: true,
						errorMessage: 'Введите стоимость',
						validationType: 'minMaxRequired',
						maxValue: 1000000000,
					},
				},
				{
					isHidden: !showFields.includes('subjectCostByContract'),
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].subjectCostByContract`,
						defaultValue: subjectCostByContract || '',
						grid: 6,
						digitsAfterDot: 2,
						placeholder: 'Стоимость объекта залога по договору купли-продажи',
					},
				},
				{
					isHidden: !isFull || !showFields.includes('agency'),
					field: {
						fieldType: 'input',
						fieldName: `${arrayName}[${number}].agency`,
						defaultValue: agency,
						placeholder: 'Агентство недвижимости, которое готовит сделку купли-продажи, ФИО и телефон агента',
						grid: 6,
					},
				},
				{
					isHidden: !isFull || !showFields.includes('nameOfAppraiser'),
					field: {
						fieldType: 'input',
						defaultValue: nameOfAppraiser,
						fieldName: `${arrayName}[${number}].nameOfAppraiser`,
						placeholder: 'Наименование назависимого оценщика',
						grid: 6,
					},
				},


				{
					isHidden: ![PROPERTY_NON_RESIDENTIAL].includes(String(pledgeIsn)),
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].purpose`,
						items: selectPurposes,
						isLoading: loadingPurposes,
						defaultValue: purpose,
						placeholder: 'Целевое назначение',
						isSearchAvailable: true,
						isClearable: true,
						isRequired: true,
					},
				},

				{
					field: {
						fieldType: 'element',
						fieldName: `${arrayName}[${number}].hr`,
						element: <></>,
					},
				},


				{
					isHidden: [PROPERTY_LAND, PROPERTY_PARKING].includes(String(pledgeIsn)),
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].alarm`,
						items: selectAlarms,
						isLoading: loadingAlarms,
						defaultValue: alarm,
						placeholder: 'Сигнализация',
						isClearable: true,
						isRequired: ALARM_IS_REQUIRED_FOR.includes(String(pledgeIsn)),
						grid: isAlarmOther ? 6 : 12,
					},
				},
				{
					isHidden: !isAlarmOther,
					field: {
						fieldType: 'input',
						isRequired: true,
						fieldName: `${arrayName}[${number}].alarmInput`,
						defaultValue: alarmInput,
						grid: 6,
						placeholder: 'Другое',
					},
				},
				{
					isHidden: [PROPERTY_LAND, PROPERTY_PARKING].includes(String(pledgeIsn)),
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].openFire`,
						items: selectFireSources,
						isLoading: loadingFireSources,
						defaultValue: openFire,
						placeholder: 'Наличие источников открытого огня',
						isClearable: true,
						isRequired: ALARM_IS_REQUIRED_FOR.includes(String(pledgeIsn)),
						grid: isOpenFireOther ? 6 : 12,
					},
				},
				{
					isHidden: !isOpenFireOther,
					field: {
						fieldType: 'input',
						isRequired: true,
						fieldName: `${arrayName}[${number}].openFireInput`,
						defaultValue: openFireInput,
						placeholder: 'Другое',
						grid: 6,
					},
				},

				{
					isHidden: [PROPERTY_LAND, PROPERTY_PARKING].includes(String(pledgeIsn)),
					field: {
						fieldType: 'select',
						fieldName: `${arrayName}[${number}].additionalRisks`,
						items: selectRiskFactors,
						isLoading: loadingRiskFactors,
						defaultValue: additionalRisks,
						placeholder: 'Дополнительные факторы риска',
						isClearable: true,
						isRequired: ALARM_IS_REQUIRED_FOR.includes(String(pledgeIsn)),
						grid: isAdditionalRisksOther ? 6 : 12,
					},
				},
				{
					isHidden: !isAdditionalRisksOther,
					field: {
						fieldType: 'input',
						isRequired: true,
						fieldName: `${arrayName}[${number}].additionalRisksInput`,
						defaultValue: additionalRisksInput,
						placeholder: 'Другое',
						grid: 6,
					},
				},

				/*{
					isHidden: !isFull,
					label: 'Объект незавершенного строительства',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].objectOfUnfinishedConstruction`,
						items: RADIO_BOOLEAN,
						defaultValue: objectOfUnfinishedConstruction,
					},
				},*/
				{
					isHidden: [PROPERTY_STORAGE, PROPERTY_PARKING, PROPERTY_LAND].includes(String(pledgeIsn)) || !showFields.includes('repairPlanning'),
					label:
						'Планируете ли Вы проведение ремонтных работ, связанных с изменениями в конструктивных элементах недвижимого имущества (перенос стен, создание перегородок и проемов в стенах, замена труб)? ',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].repairPlanning`,
						items: RADIO_BOOLEAN,
						defaultValue: repairPlanning,
					},
				},
				/*{
					isHidden: !isPlanningRepair || !isFull,
					field: {
						fieldType: 'date',
						grid: 6,
						fieldName: `${arrayName}[${number}].repairIntervalStart`,
						defaultValue: repairIntervalStart,
						placeholder: 'Дата начала ремонта',
					},
				},
				{
					isHidden: !isPlanningRepair || !isFull,
					field: {
						fieldType: 'date',
						grid: 6,
						fieldName: `${arrayName}[${number}].repairIntervalEnd`,
						defaultValue: repairIntervalEnd,
						placeholder: 'Дата окончания ремонта',
					},
				},*/
				/*{
					isHidden: !isPlanningRepair || !isFull,
					field: {
						fieldType: 'number',
						fieldName: `${arrayName}[${number}].riskCoefficient`,
						defaultValue: riskCoefficient,
						placeholder: 'Коэффициент за повышенный риск объекта страхования',
					},
				},*/
				/*{
					isHidden: !isFull,
					label: 'Подвальное или чердачное помещение?',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].basementOrAttic`,
						items: RADIO_BOOLEAN,
						defaultValue: basementOrAttic,
					},
				},*/


				/*{
					isHidden: !isFull,
					label: 'Внутрення отделка и инженерное оборудование',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].interiorDecoration`,
						defaultValue: interiorDecoration,
						items: RADIO_BOOLEAN,
					},
				},*/
				/*{
					isHidden: !isFull,
					label: 'Вы планируете или проводите перепланировку / переоборудование? Укажите сроки перепланировки / переоборудования',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].replanPlanning`,
						items: RADIO_BOOLEAN,
						defaultValue: replanPlanning,
					},
				},*/
				/*{
					isHidden: !isPlanningReplan || !isFull,
					field: {
						fieldType: 'date',
						grid: 6,
						fieldName: `${arrayName}[${number}].replanIntervalStart`,
						placeholder: 'Дана начала перепланировки / переоборудования',
						defaultValue: replanIntervalStart,
					},
				},
				{
					isHidden: !isPlanningReplan || !isFull,
					field: {
						fieldType: 'date',
						grid: 6,
						fieldName: `${arrayName}[${number}].replanIntervalEnd`,
						placeholder: 'Дана окончания перепланировки / переоборудования',
						defaultValue: replanIntervalEnd,
					},
				},*/
				{
					isHidden: [PROPERTY_STORAGE, PROPERTY_PARKING, PROPERTY_LAND, PROPERTY_GARAGE].includes(String(pledgeIsn)) || !showFields.includes('rent'),
					label: 'Будет ли недвижимое имущество сдаваться в аренду третьим лицам?',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].rent`,
						items: RADIO_BOOLEAN,
						defaultValue: rent,
					},
				},
				{
					isHidden: !isFull || !showFields.includes('damage'),
					label: 'Известны ли Вам физические повреждения объекта залога за последние 2 года, сумма ущерба которых превышала 200 000 ₽? ',
					field: {
						fieldType: 'radio',
						fieldName: `${arrayName}[${number}].damage`,
						items: RADIO_BOOLEAN,
						defaultValue: damage,
					},
				},
			],
		}),
		[
			additionalRisks,
			additionalRisksInput,
			agency,
			area,
			arrayName,
			basementOrAttic,
			cadastralNumber,
			damage,
			floor,
			floorCount,
			floorMaterial,
			formHook,
			interiorDecoration,
			isAdditionalRisksOther,
			isFlat,
			isFull,
			isOpenFireOther,
			isPlanningRepair,
			isPlanningReplan,
			isAlarmOther,
			loadingPledge,
			loadingWallMaterial,
			nameOfAppraiser,
			number,
			objectOfUnfinishedConstruction,
			openFire,
			openFireInput,
			pledge,
			pledgeAddress,
			addressMatchValue,
			pledgeAddressEgrn,
			pledgeIsn,
			pledgeShare,
			rent,
			repairIntervalEnd,
			repairIntervalStart,
			repairPlanning,
			replanIntervalEnd,
			replanIntervalStart,
			replanPlanning,
			riskCoefficient,
			roomsCount,
			selectPledge,
			selectWallMaterial,
			selectFloorMaterial,
			alarmInput,
			stateAddressEgrn,
			subjectCost,
			subjectCostByContract,
			wallMaterial,
			yearOfBuild,
			yearOfCapitalRepair,
			yearOfBuildValue,
		]
	);

	const value: PropertyContext = React.useMemo(
		() => ({
			form,
		}),
		[form]
	);

	return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
};

export default React.memo(PropertyContextProvider);
